import type {
  HeaderAnalyticPayload,
  HeaderV4Props,
} from '@jouzen/ecom-components/layout';
import {
  defaultHeaderData,
  defaultHeaderSecondaryLinks,
  useHeaderContext,
} from '@jouzen/ecom-components/layout';
import { useRouter } from 'next/navigation';
import { useCallback } from 'react';

import { useCartContext } from '@/app/contexts/CartProvider';
import useIsForOrganizationsEnabled from '@/app/hooks/useIsForOrganizationsEnabled';
import useIsNomadEnabled from '@/app/hooks/useIsNomadEnabled';
import useIsSteloEnabled from '@/app/hooks/useIsSteloEnabled';
import useUsaOnly from '@/app/hooks/useUsaOnly';

interface UseHeaderPropsOptions {
  currentPath: string;
  enableScrollAnimation: boolean;
  isCarouselInView: boolean;
  locale: string;
  onHamburgerClick: ({ open }: { open: boolean }) => void;
  onMenuLinkClick: (_: unknown, payload: HeaderAnalyticPayload) => void;
  parentHeaderProps?: Partial<HeaderV4Props> | undefined;
}

const useHeaderProps = ({
  currentPath,
  enableScrollAnimation,
  isCarouselInView,
  locale,
  onHamburgerClick,
  onMenuLinkClick,
  parentHeaderProps,
}: UseHeaderPropsOptions): HeaderV4Props => {
  const { inverse } = useHeaderContext()!;
  const router = useRouter();
  const { refreshCart } = useCartContext();
  const cart = refreshCart?.cart;

  const isSteloEnabled = useIsSteloEnabled();
  const isUSAOnly = useUsaOnly();
  const isForOrganizationsEnabled = useIsForOrganizationsEnabled();
  const isNomadEnabled = useIsNomadEnabled();

  const handleShoppingCartClick = useCallback((): void => {
    router.push(`/${locale}/cart`);
  }, [locale, router]);

  const headerPosition: HeaderV4Props['headerPosition'] =
    parentHeaderProps?.headerPosition === 'static' ? 'static' : 'fold';

  return {
    count: cart?.itemCountForIcon ?? 0,
    currentPath,
    enableScrollAnimation,
    headerConfig: defaultHeaderData({
      isGiftCardEnabled: isUSAOnly,
      isSteloEnabled,
      isForOrganizationsEnabled,
      isNomadEnabled,
    }),
    headerPosition,
    inverse,
    isCarouselInView,
    onHamburgerClick,
    onMenuLinkClick,
    onShoppingCartClick: handleShoppingCartClick,
    secondaryLinks: defaultHeaderSecondaryLinks,
    ...parentHeaderProps,
  };
};

export default useHeaderProps;
